import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  StagingBlocker,
  StagingIssue,
} from '../../../common/model/type/video-staging.types';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoUploadValidationProps {
  id: string;
  blocker: StagingBlocker;
  readyCount: number;
}

const focusField = ({ fieldId }: StagingIssue) =>
  document.getElementById(fieldId)?.focus();

const OvVideoUploadValidation: FC<OvVideoUploadValidationProps> = ({
  id,
  blocker,
  readyCount,
}) => {
  const { t } = useTranslation();

  switch (blocker.kind) {
    case 'none':
      return (
        <Ready id={id}>
          {t('videos.staging.readyCount', { count: readyCount })}
        </Ready>
      );

    case 'noFiles':
      return <Blocked id={id}>{t('videos.staging.noFiles')}</Blocked>;

    case 'noPermission':
      return <Blocked id={id}>{t('videos.staging.noPermission')}</Blocked>;

    case 'reading':
      return <Ready id={id}>{t('videos.staging.readingSize')}</Ready>;

    case 'issues':
      return (
        <Blocked id={id}>
          {t('videos.staging.issuesCount', { count: blocker.issues.length })}
          <Issues>
            {blocker.issues.map((issue) => (
              <li key={issue.fieldId}>
                <IssueLink type="button" onClick={() => focusField(issue)}>
                  {t(`videos.staging.issues.${issue.kind}`, {
                    name: issue.fileName,
                  })}
                </IssueLink>
              </li>
            ))}
          </Issues>
        </Blocked>
      );

    default: {
      const unhandledBlocker: never = blocker;

      return unhandledBlocker;
    }
  }
};

export default OvVideoUploadValidation;

const Ready = styled.div`
  font-size: ${Variables.fontSizes.MEDIUM};
  color: ${Colours.OV_CHARCOAL};
`;

const Blocked = styled.div`
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
  color: ${Colours.OV_ERROR_ACCESSIBLE};
`;

const Issues = styled.ul`
  margin: 0.25rem 0 0;
  padding-left: 1.25rem;
  font-weight: normal;
`;

const IssueLink = styled.button`
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid ${Colours.OV_ACCENT_BLUE};
    outline-offset: 2px;
  }
`;
